import { ApiError, api } from '@/lib/api';
import { openAdmin } from '@/lib/overlays';
import { queryClient } from '@/lib/queryClient';
import { toast, toastError } from '@/lib/toast';
import type { BackupItem } from '@shared/backups';
import { BASE, downloadFrom } from './api';

// Starting backups and downloads. Kept apart from the screens so the search palette can back up without loading them.

/** Explains a refusal and offers the place that fixes it. */
export function explainRefusal(err: unknown): void {
  if (err instanceof ApiError && err.code === 'no_backup_key') {
    toast(err.message, { icon: 'key', action: { label: 'Create a key pair', run: () => openAdmin('keys') } });
  } else if (err instanceof ApiError && err.code === 'storage_not_configured') {
    toast(err.message, { icon: 'cloud', action: { label: 'Connect S3', run: () => openAdmin('files') } });
  } else {
    toastError(err, 'The backup could not start. Try again.');
  }
}

function refresh(): void {
  void queryClient.invalidateQueries({ queryKey: ['admin', 'backups'] });
}

/** Starts a backup to S3. Its progress shows on the Backups page and a toast follows when it finishes. */
export async function startBackup(): Promise<BackupItem | null> {
  try {
    const item = await api.post<BackupItem>(`${BASE}/run`);
    refresh();
    return item;
  } catch (err) {
    explainRefusal(err);
    return null;
  }
}

/** "Back up now" from anywhere: shows the Backups page, where the progress appears. */
export function backupNow(): void {
  openAdmin('backups');
  void startBackup();
}

/** A fresh backup, made while it downloads. Works without S3. */
export async function downloadFresh(): Promise<void> {
  try {
    const { url } = await api.post<{ url: string }>(`${BASE}/download`);
    downloadFrom(url);
    toast('Backing up into your download · locked, opens only with the private key', { icon: 'download' });
    setTimeout(refresh, 800);
  } catch (err) {
    explainRefusal(err);
  }
}

/** A stored backup: a short-lived S3 link, or the copy kept on the server. */
export async function downloadStored(item: Pick<BackupItem, 'id' | 'fileName'>): Promise<void> {
  try {
    const { url } = await api.post<{ url: string }>(`${BASE}/${item.id}/download`);
    downloadFrom(url);
    toast(`Downloading ${item.fileName ?? 'the backup'} · locked, opens only with the private key`, { icon: 'download' });
  } catch (err) {
    toastError(err, 'The download could not start. Try again.');
  }
}
